import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { getScrollContainer } from '@theme/scroll-container';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * One entry of a card's variant map, emitted by `look-builder-item.liquid`.
 *
 * @typedef {object} LookVariant
 * @property {number} id - Variant id sent to the Cart API.
 * @property {string[]} options - Option values, in product option order.
 * @property {boolean} available - Whether the variant can be purchased.
 * @property {number} price - Price in minor units.
 * @property {string} priceFormatted - Price rendered with the shop's format.
 * @property {string} thumb - Variant image sized for the card thumbnail.
 * @property {string} featureImage - Variant image sized for the large media slot.
 * @property {string} imageAlt - Alt text for both variant images.
 */

/**
 * A section component that lets customers assemble a set of products across
 * category tabs and add every selected variant to the cart in one request.
 *
 * State lives in the DOM (`data-selected`, checked radios), so the component
 * holds no duplicated model: the summary and total are recomputed from the
 * selected items on every change.
 *
 * Gilu customization: selecting a tab card opens its "Complete the look"
 * sub-panel (`look-builder-complements.liquid`), a sibling of the card whose
 * complement cards are ordinary items marked `data-look-complement`. They join
 * the look like any other piece; deselecting the parent closes the panel and
 * drops them. A product is in the look at most once: selecting a piece
 * deselects any other selected card of the same product, whether it is a tab
 * card or a complement under another parent. Customers can collapse a
 * complementary-products panel without losing its selected pieces, then open
 * it again from the parent card to review or change them.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tabs - The tab buttons, rendered by each block.
 * @property {HTMLElement[]} panels - The tab panels, paired to tabs by id.
 * @property {HTMLElement} tablist - Destination the tab buttons are moved into.
 * @property {HTMLElement} summaryItems - Container for the selected-item rows.
 * @property {HTMLTemplateElement} summaryRowTemplate - Row markup to clone.
 * @property {HTMLElement} summaryEmpty - Empty-state message.
 * @property {HTMLElement} totalValue - The formatted set total.
 * @property {HTMLButtonElement} addButton - The add-set-to-cart button.
 * @property {HTMLElement} errorMessage - Inline error live region.
 * @property {HTMLImageElement} [mediaImage] - Large image beside the picker.
 *
 * @extends {Component<Refs>}
 */
class LookBuilderComponent extends Component {
  requiredRefs = ['summaryItems', 'summaryRowTemplate', 'summaryEmpty', 'totalValue', 'addButton', 'errorMessage'];

  #busy = false;

  /** Increments on each selection so the newest one can be identified. */
  #selectionSequence = 0;

  /** The merchant's image, restored when nothing is selected. */
  #defaultMedia = null;

  /**
   * Parsed variant maps, keyed by card: the JSON is inert markup, so parsing it
   * once per card keeps every option click off the parser.
   * @type {WeakMap<HTMLElement, LookVariant[]>}
   */
  #variantMaps = new WeakMap();

  /**
   * "Complete the look" rows that already keep their arrows in sync on scroll.
   * @type {WeakSet<HTMLElement>}
   */
  #watchedComplementLists = new WeakSet();

  /**
   * Scroll positions captured before a pointer activates a custom variant
   * radio. Native label activation can focus the visually hidden input and
   * move both the look-builder frame and Horizon's page scroll container.
   * @type {{panel: HTMLElement | null, panelTop: number, panelLeft: number, page: Element, pageTop: number, pageLeft: number} | null}
   */
  #variantScrollSnapshot = null;

  connectedCallback() {
    super.connectedCallback();

    const { mediaImage } = this.refs;
    if (mediaImage) {
      // Attributes, not properties: `img.src` resolves to an absolute URL,
      // which would never match the protocol-relative URLs Liquid emits.
      this.#defaultMedia = {
        src: mediaImage.getAttribute('src') ?? '',
        srcset: mediaImage.getAttribute('srcset') ?? '',
        alt: mediaImage.getAttribute('alt') ?? '',
      };
    }

    this.#collectTabs();
    this.#activateTab(this.refs.tabs?.[0]);
    this.#update();

    // In the theme editor, reveal the tab whose block the merchant selected.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onEditorBlockSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('shopify:block:select', this.#onEditorBlockSelect);
  }

  /** @param {Event} event */
  #onEditorBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const panel = target.closest('[role="tabpanel"]');
    if (!(panel instanceof HTMLElement)) return;

    this.#activateTab(this.refs.tabs?.find((tab) => tab.getAttribute('aria-controls') === panel.id));
  };

  /**
   * Each block renders its tab button next to its own panel, since block
   * settings (the merchant's title) only resolve inside the block. Gather
   * them into the tablist so the ARIA tabs pattern holds.
   */
  #collectTabs() {
    const { tablist } = this.refs;
    if (!tablist) return;

    for (const tab of this.querySelectorAll('[data-look-tab]')) {
      if (tab.parentElement !== tablist) tablist.appendChild(tab);
    }
  }

  /**
   * Shows the clicked tab's panel and hides the rest. Selections are kept when
   * switching tabs — hiding a panel must not clear the customer's look.
   * @param {PointerEvent} event
   */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement) this.#activateTab(tab);
  }

  /**
   * Panels are matched through `aria-controls`, not list position, so moving
   * buttons around cannot desynchronise a tab from its panel.
   * @param {HTMLElement | undefined} tab
   */
  #activateTab(tab) {
    if (!tab) return;

    const targetId = tab.getAttribute('aria-controls');
    if (!targetId) return;

    for (const button of this.refs.tabs ?? []) {
      const isActive = button === tab;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const panel of this.refs.panels ?? []) {
      panel.toggleAttribute('hidden', panel.id !== targetId);
    }
  }

  /**
   * Roving focus for the tablist, per the ARIA tabs pattern.
   * @param {KeyboardEvent} event
   */
  onTablistKeydown(event) {
    const { tabs } = this.refs;
    const current = tabs.findIndex((tab) => tab.getAttribute('tabindex') === '0');
    if (current === -1) return;

    let next = null;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (next === null) return;

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    tab.focus();
    tab.click();
  }

  /**
   * Toggles an item card. Items with variants first expand their option list
   * and only join the look once a variant is picked, so a size the customer
   * never chose can never reach the cart.
   * @param {PointerEvent} event
   */
  toggleItem(event) {
    const item = event.target instanceof Element ? event.target.closest('[data-look-item]') : null;
    if (!(item instanceof HTMLElement)) return;

    if (item.dataset.selected === 'true') {
      this.#deselect(item);
      this.#update();
      return;
    }

    const options = this.#optionsForItem(item);

    // The card only carries a variant id once every option resolves to an
    // available variant, so this covers both "nothing picked" and "size picked
    // but colour still missing".
    if (options && !item.dataset.variantId) {
      // Complement tiles share one full-width option tray so cards never grow
      // to the height of their Size/Colour/Style controls.
      if (item.hasAttribute('data-look-complement')) {
        this.#openComplementOptions(item, options);
      } else {
        this.#setOptionsExpanded(item, true);
      }
      const firstEnabled = options.querySelector('input:not(:disabled)');
      // The options live inside a bounded scroll container. Native focus would
      // scroll that frame to the visually hidden radio and make the card jump.
      if (firstEnabled instanceof HTMLElement) firstEnabled.focus({ preventScroll: true });
      return;
    }

    this.#select(item);
  }

  /**
   * Called when any option value is picked. Options are rendered one row per
   * product option, so a choice is a partial combination: this resolves it
   * against the card's variant map, re-marks which values are still reachable,
   * and only joins the look once the combination lands on an available variant.
   * @param {Event} event
   */
  selectOption(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const item = this.#itemForOptions(input);
    if (!(item instanceof HTMLElement)) return;

    const variants = this.#variants(item);
    const chosen = this.#chosenOptions(item);

    this.#markReachableOptions(item, variants, chosen);
    this.#previewVariantMedia(item, variants, chosen);

    const complete = chosen.every((value) => value !== null);
    const match = complete
      ? variants.find((variant) => variant.options.every((value, index) => value === chosen[index]))
      : undefined;

    const priceLabel = item.querySelector('[data-look-item-price]');

    if (!match || !match.available) {
      // An unreachable combination must not stay in the look: drop the variant
      // and say why, rather than silently keeping the previous one. The option
      // list stays open so the customer can pivot, but the sub-panel closes:
      // its pieces complete a look this card has just left.
      delete item.dataset.variantId;
      item.dataset.selected = 'false';
      this.#setVariantNotice(item, !complete);
      this.#setComplementsOpen(item, false, true);
      if (priceLabel && complete) priceLabel.textContent = item.dataset.soldOutLabel ?? '';
      this.#update();
      this.#restoreVariantScroll();
      return;
    }

    item.dataset.variantId = String(match.id);
    item.dataset.price = String(match.price);
    this.#setVariantNotice(item, false);
    if (priceLabel) priceLabel.textContent = match.priceFormatted;

    if (item.hasAttribute('data-look-complement')) this.#setOptionsExpanded(item, false);
    this.#select(item);
    this.#restoreVariantScroll();
  }

  /**
   * Captures both scroll layers before native radio focus can move them.
   * @param {PointerEvent} event
   */
  preserveVariantScroll(event) {
    const option = event.target instanceof Element ? event.target.closest('.look-builder__option') : null;
    if (!(option instanceof HTMLElement)) return;

    const panel = option.closest('.look-builder__panels');
    const page = getScrollContainer();
    const snapshot = {
      panel: panel instanceof HTMLElement ? panel : null,
      panelTop: panel instanceof HTMLElement ? panel.scrollTop : 0,
      panelLeft: panel instanceof HTMLElement ? panel.scrollLeft : 0,
      page,
      pageTop: page.scrollTop,
      pageLeft: page.scrollLeft,
    };

    this.#variantScrollSnapshot = snapshot;
    setTimeout(() => {
      if (this.#variantScrollSnapshot === snapshot) this.#variantScrollSnapshot = null;
    }, 500);
  }

  /** Restores a pointer selection without animated scrolling or visible drift. */
  #restoreVariantScroll() {
    const snapshot = this.#variantScrollSnapshot;
    if (!snapshot) return;
    this.#variantScrollSnapshot = null;

    const restore = () => {
      if (snapshot.panel?.isConnected) {
        snapshot.panel.scrollTop = snapshot.panelTop;
        snapshot.panel.scrollLeft = snapshot.panelLeft;
      }
      snapshot.page.scrollTop = snapshot.pageTop;
      snapshot.page.scrollLeft = snapshot.pageLeft;
    };

    restore();
    requestAnimationFrame(restore);
  }

  /**
   * Adds every selected variant to the cart in a single Cart API request,
   * mirroring the theme's batch add flow so the cart drawer opens and
   * re-renders through the standard events.
   */
  async addSetToCart() {
    if (this.#busy) return;

    const selectedItems = this.#selectedItems();
    if (selectedItems.length === 0) return;

    const lines = selectedItems.map((item) => ({
      merchandiseId: item.dataset.variantId ?? '',
      quantity: 1,
    }));

    const sectionIds = [];
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.push(cartItems.dataset.sectionId);
      }
    }

    this.#setBusy(true);
    this.#showError(null);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: lines.map((line) => ({ id: Number(line.merchandiseId), quantity: line.quantity })),
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        this.#showError(result.message || this.dataset.errorFallback || '');
        this.#resolveWithCart(deferred, { didError: true, itemCount: lines.length });
        return;
      }

      this.#resolveWithCart(deferred, {
        didError: false,
        itemCount: lines.length,
        sections: result.sections,
      });

      // A fresh start after a successful add: pressing the button again must
      // not re-add the same pieces. Only what gets selected next is sent.
      this.#clearSelection();
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.dataset.errorFallback || '');
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Fetches the post-mutation cart and resolves the deferred event promise the
   * cart drawer and cart items components are waiting on.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferred
   * @param {{didError: boolean, itemCount: number, sections?: Record<string, string>}} detail
   */
  #resolveWithCart(deferred, { didError, itemCount, sections }) {
    this.#fetchCart()
      .then((ajaxCart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError,
            items: ajaxCart.items,
            source: 'look-builder-component',
            sourceId: this.id.toString(),
            itemCount,
            sections,
          },
        });
      })
      .catch(deferred.reject);
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Marks an item selected, enforcing its panel's selection mode and the
   * one-piece-per-product rule, and opens a tab card's sub-panel.
   * @param {HTMLElement} item
   */
  #select(item) {
    const isComplement = item.hasAttribute('data-look-complement');
    const panel = item.closest('[data-selection-mode]');

    // Complements are extras on top of the tab's choice, so "single" only
    // makes the tab's own cards exclusive.
    if (!isComplement && panel instanceof HTMLElement && panel.dataset.selectionMode === 'single') {
      for (const sibling of panel.querySelectorAll('[data-look-item][data-selected="true"]:not([data-look-complement])')) {
        if (sibling === item) continue;
        this.#deselect(/** @type {HTMLElement} */ (sibling));
      }
    }

    // The same product can be a tab card and a complement (or a complement
    // under two parents). Keeping one selected card per product means the
    // summary never lists a piece twice and the set never adds it twice by
    // accident; the newest pick wins, the way a radio choice would.
    const productId = item.dataset.productId;
    if (productId) {
      for (const other of this.querySelectorAll(`[data-look-item][data-selected="true"][data-product-id="${productId}"]`)) {
        if (other === item) continue;
        this.#deselect(/** @type {HTMLElement} */ (other));
      }
    }

    item.dataset.selected = 'true';
    item.dataset.selectedAt = String(++this.#selectionSequence);
    if (!isComplement) this.#setComplementsOpen(item, true);
    this.#update();
  }

  /**
   * Removes an item from the look, collapsing its options and closing its
   * sub-panel. Callers refresh the summary with #update.
   * @param {HTMLElement} item
   */
  #deselect(item) {
    item.dataset.selected = 'false';
    this.#setOptionsExpanded(item, false);
    this.#setComplementsOpen(item, false, true);
  }

  /**
   * Shows or hides a tab card's "Complete the look" sub-panel. A customer-led
   * collapse preserves its pieces; deselecting the parent clears them because
   * they no longer belong to the active look.
   * @param {HTMLElement} item
   * @param {boolean} open
   * @param {boolean} [clearSelection]
   */
  #setComplementsOpen(item, open, clearSelection = false) {
    const panelId = item.dataset.lookComplementsId;
    if (!panelId) return;

    const panel = document.getElementById(panelId);
    if (!(panel instanceof HTMLElement) || !this.contains(panel)) return;

    panel.hidden = !open;
    item.dataset.lookComplementsOpen = open ? 'true' : 'false';

    if (!open) {
      const activeOptions = panel.querySelector('[data-look-variant-tray] [data-look-options]:not([hidden])');
      if (activeOptions instanceof HTMLElement) {
        const activeItem = this.#itemForOptions(activeOptions);
        if (activeItem) this.#setOptionsExpanded(activeItem, false);
      }

      if (clearSelection) {
        for (const complement of panel.querySelectorAll('[data-look-item][data-selected="true"]')) {
          /** @type {HTMLElement} */ (complement).dataset.selected = 'false';
          this.#setOptionsExpanded(/** @type {HTMLElement} */ (complement), false);
        }
      }
      return;
    }

    // Overflow can only be measured once the panel is laid out.
    this.#watchComplementsScroll(panel);
    requestAnimationFrame(() => {
      this.#syncComplementsNav(panel);
      this.#revealInPanelFrame(panel);
    });
  }

  /** Collapses a complementary-products panel without changing the look. */
  collapseComplements(event) {
    const panel = event.target instanceof Element ? event.target.closest('[data-look-complements]') : null;
    if (!(panel instanceof HTMLElement)) return;

    const parentId = panel.dataset.parentProductId;
    const parent = Array.from(this.querySelectorAll('[data-look-item]:not([data-look-complement])')).find(
      (item) => item instanceof HTMLElement && item.dataset.productId === parentId
    );
    if (!(parent instanceof HTMLElement)) return;

    this.#setComplementsOpen(parent, false);
    const reopen = parent.querySelector('[data-look-complements-reopen]');
    if (reopen instanceof HTMLElement) reopen.focus({ preventScroll: true });
  }

  /** Reopens the preserved complementary-products panel from its parent card. */
  reopenComplements(event) {
    const item = event.target instanceof Element ? event.target.closest('[data-look-item]') : null;
    if (!(item instanceof HTMLElement) || item.dataset.selected !== 'true') return;

    this.#setComplementsOpen(item, true);
    const panelId = item.dataset.lookComplementsId;
    const panel = panelId ? document.getElementById(panelId) : null;
    const firstControl = panel?.querySelector('[data-look-item-toggle]');
    if (firstControl instanceof HTMLElement) firstControl.focus({ preventScroll: true });
  }

  /**
   * Scrolls a "Complete the look" row by one tile in the button's direction.
   * @param {PointerEvent} event
   */
  scrollComplements(event) {
    const button = event.target instanceof Element ? event.target.closest('button[data-direction]') : null;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-disabled') === 'true') return;

    const list = button.closest('[data-look-complements]')?.querySelector('[data-look-complements-list]');
    if (!(list instanceof HTMLElement)) return;

    const tile = list.querySelector('[data-look-item]');
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const step = tile instanceof HTMLElement ? tile.offsetWidth + gap : list.clientWidth;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    list.scrollBy({ left: Number(button.dataset.direction) * step, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /**
   * Keeps a row's arrows in sync while the customer swipes or scrolls it.
   * Scroll does not bubble, so it cannot go through declarative `on:` binding.
   * @param {HTMLElement} panel
   */
  #watchComplementsScroll(panel) {
    const list = panel.querySelector('[data-look-complements-list]');
    if (!(list instanceof HTMLElement) || this.#watchedComplementLists.has(list)) return;

    this.#watchedComplementLists.add(list);
    list.addEventListener('scroll', () => this.#syncComplementsNav(panel), { passive: true });
  }

  /**
   * Shows the arrows only when the row overflows, and marks the one pointing
   * past either end as unavailable.
   * @param {HTMLElement} panel
   */
  #syncComplementsNav(panel) {
    const list = panel.querySelector('[data-look-complements-list]');
    const nav = panel.querySelector('[data-look-complements-nav]');
    if (!(list instanceof HTMLElement) || !(nav instanceof HTMLElement)) return;

    const overflows = list.scrollWidth > list.clientWidth + 1;
    nav.hidden = !overflows;
    if (!overflows) return;

    for (const button of nav.querySelectorAll('button[data-direction]')) {
      const atEnd =
        Number(/** @type {HTMLElement} */ (button).dataset.direction) < 0
          ? list.scrollLeft <= 1
          : list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
      button.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }

  /**
   * Mirrors the most recent selection in the large media slot, falling back to
   * the merchant's image once the look is emptied.
   * @param {HTMLElement[]} selectedItems
   */
  #updateMedia(selectedItems) {
    const { mediaImage } = this.refs;
    if (!mediaImage || !this.#defaultMedia) return;

    const newest = selectedItems.reduce((latest, item) => {
      const at = Number(item.dataset.selectedAt) || 0;
      return at > (Number(latest?.dataset.selectedAt) || 0) ? item : latest;
    }, /** @type {HTMLElement | undefined} */ (undefined));

    const src = newest?.dataset.featureImage;
    const next = src
      ? { src, srcset: src, alt: newest?.dataset.featureImageAlt ?? '' }
      : this.#defaultMedia;

    if (mediaImage.getAttribute('src') === next.src) return;

    // srcset is replaced rather than cleared: it outranks src, so leaving a
    // stale one makes the browser re-pick the previous candidate.
    mediaImage.setAttribute('srcset', next.srcset);
    mediaImage.setAttribute('src', next.src);
    mediaImage.setAttribute('alt', next.alt);
  }

  /**
   * Swaps the media as soon as a partial combination already determines it —
   * picking a colour is enough, no size needed. The image is only applied when
   * every variant still matching agrees on it, so choosing a size while the
   * colour is undecided leaves the card alone instead of guessing a colour.
   * @param {HTMLElement} item
   * @param {LookVariant[]} variants
   * @param {(string | null)[]} chosen
   */
  #previewVariantMedia(item, variants, chosen) {
    if (chosen.every((value) => value === null)) return;

    const candidates = variants.filter((variant) =>
      chosen.every((value, index) => value === null || variant.options[index] === value)
    );

    const [first] = candidates;
    if (!first) return;
    if (!candidates.every((variant) => variant.featureImage === first.featureImage)) return;

    this.#applyVariantMedia(item, first);
  }

  /**
   * Swaps the card thumbnail — and the large media slot this card feeds — to
   * the picked variant's image. Variants without their own media fall back to
   * the product image in Liquid, so there is always a src to apply.
   * @param {HTMLElement} item
   * @param {LookVariant} variant
   */
  #applyVariantMedia(item, variant) {
    if (variant.featureImage) {
      item.dataset.featureImage = variant.featureImage;
      item.dataset.featureImageAlt = variant.imageAlt || item.dataset.title || '';
    }

    const thumb = item.querySelector('.look-builder__item-media img');
    if (!(thumb instanceof HTMLImageElement) || !variant.thumb) return;
    if (thumb.getAttribute('src') === variant.thumb) return;

    // srcset outranks src, so a stale one would make the browser re-pick the
    // previous candidate: rewrite the whole descriptor set from this image.
    const base = variant.thumb;
    thumb.setAttribute(
      'srcset',
      `${base} 1x, ${base.replace(/height=\d+/, 'height=240')} 2x, ${base.replace(/height=\d+/, 'height=360')} 3x`
    );
    thumb.setAttribute('src', base);
    thumb.setAttribute('alt', variant.imageAlt || item.dataset.title || '');
  }

  /**
   * The card's variant map, parsed on first use.
   * @param {HTMLElement} item
   * @returns {LookVariant[]}
   */
  #variants(item) {
    const cached = this.#variantMaps.get(item);
    if (cached) return cached;

    const source = this.#optionsForItem(item)?.querySelector('[data-look-variants]');
    let variants = [];

    try {
      const parsed = JSON.parse(source?.textContent ?? '[]');
      if (Array.isArray(parsed)) variants = parsed;
    } catch {
      // A malformed map must not take the whole section down: the card simply
      // never resolves a variant, so it stays unselectable.
      variants = [];
    }

    this.#variantMaps.set(item, variants);
    return variants;
  }

  /**
   * The currently picked value of each option row, in product option order.
   * `null` marks a row the customer has not answered yet.
   * @param {HTMLElement} item
   * @returns {(string | null)[]}
   */
  #chosenOptions(item) {
    const options = this.#optionsForItem(item);
    return Array.from(options?.querySelectorAll('[data-look-option-group]') ?? [], (group) => {
      const checked = group.querySelector('input:checked');
      return checked instanceof HTMLInputElement ? checked.value : null;
    });
  }

  /**
   * Marks every option value that no available variant can still reach, given
   * the choices made in the other rows. Values stay clickable on purpose: with
   * two rows, disabling them outright can strand the customer on a combination
   * they cannot leave without guessing which row to change first.
   * @param {HTMLElement} item
   * @param {LookVariant[]} variants
   * @param {(string | null)[]} chosen
   */
  #markReachableOptions(item, variants, chosen) {
    const groups = this.#optionsForItem(item)?.querySelectorAll('[data-look-option-group]') ?? [];

    groups.forEach((group, groupIndex) => {
      for (const input of group.querySelectorAll('input')) {
        if (!(input instanceof HTMLInputElement)) continue;

        const reachable = variants.some((variant) => {
          if (!variant.available) return false;
          if (variant.options[groupIndex] !== input.value) return false;
          // Every other row the customer already answered has to still hold.
          return chosen.every((value, index) => index === groupIndex || value === null || variant.options[index] === value);
        });

        input.setAttribute('aria-disabled', reachable ? 'false' : 'true');
        input.closest('.look-builder__option')?.classList.toggle('look-builder__option--unavailable', !reachable);
      }
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setOptionsExpanded(item, expanded) {
    const options = this.#optionsForItem(item);
    if (options) options.toggleAttribute('hidden', !expanded);
    this.#setVariantNotice(item, expanded && !item.dataset.variantId);

    const tray = options?.closest('[data-look-variant-tray]');
    if (tray instanceof HTMLElement) {
      tray.toggleAttribute('hidden', !expanded);
      item.dataset.optionsOpen = expanded ? 'true' : 'false';
    }

    // Values sold out across the whole product have to read as such before the
    // customer picks anything, not only after the first click.
    if (options && expanded) {
      this.#markReachableOptions(item, this.#variants(item), this.#chosenOptions(item));
    }

    const toggle = item.querySelector('[data-look-item-toggle]');
    if (toggle && options) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    const edit = item.querySelector('[data-look-variant-edit]');
    if (edit instanceof HTMLElement) edit.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Returns an item's option fieldset even while it is portalled into the
   * shared complementary-products tray.
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #optionsForItem(item) {
    const local = item.querySelector('[data-look-options]');
    if (local instanceof HTMLElement) return local;

    const key = item.dataset.lookItemKey;
    if (!key) return null;
    return (
      Array.from(this.querySelectorAll('[data-look-options-owner]')).find(
        (options) => options instanceof HTMLElement && options.dataset.lookOptionsOwner === key
      ) ?? null
    );
  }

  /**
   * Resolves the owning card for an option control inside the shared tray.
   * @param {Element} control
   * @returns {HTMLElement | null}
   */
  #itemForOptions(control) {
    const local = control.closest('[data-look-item]');
    if (local instanceof HTMLElement) return local;

    const options = control.closest('[data-look-options-owner]');
    const key = options instanceof HTMLElement ? options.dataset.lookOptionsOwner : null;
    if (!key) return null;
    return (
      Array.from(this.querySelectorAll('[data-look-item-key]')).find(
        (item) => item instanceof HTMLElement && item.dataset.lookItemKey === key
      ) ?? null
    );
  }

  /** Opens one complementary product's options in the panel-wide tray. */
  #openComplementOptions(item, options) {
    const panel = item.closest('[data-look-complements]');
    const tray = panel?.querySelector('[data-look-variant-tray]');
    const slot = tray?.querySelector('[data-look-variant-tray-slot]');
    if (!(tray instanceof HTMLElement) || !(slot instanceof HTMLElement)) return;

    const activeOptions = tray.querySelector('[data-look-options]:not([hidden])');
    if (activeOptions instanceof HTMLElement && activeOptions !== options) {
      const activeItem = this.#itemForOptions(activeOptions);
      if (activeItem) this.#setOptionsExpanded(activeItem, false);
    }

    // Cache the map before moving its script outside the card.
    this.#variants(item);
    slot.appendChild(options);
    const title = tray.querySelector('[data-look-variant-tray-title]');
    if (title) title.textContent = item.dataset.title ?? '';
    tray.hidden = false;
    this.#setOptionsExpanded(item, true);
    requestAnimationFrame(() => this.#revealInPanelFrame(tray));
  }

  /**
   * Scrolls only the bounded look-builder frame, and only by the amount needed
   * to reveal an expanded panel. The page itself never moves.
   * @param {HTMLElement} element
   */
  #revealInPanelFrame(element) {
    const scroller = element.closest('.look-builder__panels');
    if (!(scroller instanceof HTMLElement)) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const edgePadding = 8;
    const visibleHeight = scrollerRect.height - edgePadding * 2;
    let delta = 0;

    if (elementRect.height > visibleHeight) {
      delta = elementRect.top - scrollerRect.top - edgePadding;
    } else if (elementRect.bottom > scrollerRect.bottom - edgePadding) {
      delta = elementRect.bottom - scrollerRect.bottom + edgePadding;
    } else if (elementRect.top < scrollerRect.top + edgePadding) {
      delta = elementRect.top - scrollerRect.top - edgePadding;
    }

    if (Math.abs(delta) < 1) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** Reopens a selected complementary product's shared option tray. */
  editComplementOptions(event) {
    const item = event.target instanceof Element ? event.target.closest('[data-look-item]') : null;
    if (!(item instanceof HTMLElement)) return;
    const options = this.#optionsForItem(item);
    if (options) this.#openComplementOptions(item, options);
  }

  /** Closes the shared tray; the warning remains hidden until another attempt. */
  closeComplementOptions(event) {
    const tray = event.target instanceof Element ? event.target.closest('[data-look-variant-tray]') : null;
    if (!(tray instanceof HTMLElement)) return;

    const options = tray.querySelector('[data-look-options]:not([hidden])');
    const item = options instanceof HTMLElement ? this.#itemForOptions(options) : null;
    if (item) {
      this.#setOptionsExpanded(item, false);
      const returnControl = item.querySelector(
        item.dataset.selected === 'true' ? '[data-look-variant-edit]' : '[data-look-item-toggle]'
      );
      if (returnControl instanceof HTMLElement) returnControl.focus({ preventScroll: true });
    } else {
      tray.hidden = true;
    }
  }

  /**
   * Explains why a product with unresolved options has not joined the look.
   * @param {HTMLElement} item
   * @param {boolean} visible
   */
  #setVariantNotice(item, visible) {
    const notice = this.#optionsForItem(item)?.querySelector('[data-look-variant-notice]');
    if (notice instanceof HTMLElement) notice.toggleAttribute('hidden', !visible);
  }

  /**
   * Deselects every item and closes every sub-panel. Chosen sizes stay checked
   * as a convenience.
   */
  #clearSelection() {
    for (const item of this.#selectedItems()) {
      this.#deselect(item);
    }
    for (const panel of this.querySelectorAll('[data-look-complements]')) {
      panel.toggleAttribute('hidden', true);
    }
    this.#update();
  }

  /** @returns {HTMLElement[]} */
  #selectedItems() {
    return Array.from(this.querySelectorAll('[data-look-item][data-selected="true"]'));
  }

  /** Rebuilds the summary rows and total from the currently selected items. */
  #update() {
    const { summaryItems, summaryRowTemplate, summaryEmpty, totalValue, addButton } = this.refs;
    const selectedItems = this.#selectedItems();

    summaryItems.textContent = '';
    let total = 0;

    for (const item of selectedItems) {
      total += Number(item.dataset.price) || 0;

      const row = summaryRowTemplate.content.cloneNode(true);
      if (!(row instanceof DocumentFragment)) continue;

      const name = row.querySelector('[data-summary-name]');
      const price = row.querySelector('[data-summary-price]');
      // textContent keeps merchant/product strings inert — never innerHTML.
      if (name) name.textContent = item.dataset.title ?? '';
      if (price) price.textContent = this.#formatMoney(Number(item.dataset.price) || 0);
      summaryItems.appendChild(row);
    }

    this.#updateMedia(selectedItems);

    summaryEmpty.toggleAttribute('hidden', selectedItems.length > 0);
    totalValue.textContent = this.#formatMoney(total);
    addButton.disabled = this.#busy || selectedItems.length === 0;

    for (const item of this.querySelectorAll('[data-look-item]')) {
      const toggle = item.querySelector('[data-look-item-toggle]');
      if (toggle instanceof HTMLElement && item instanceof HTMLElement) {
        toggle.setAttribute('aria-pressed', item.dataset.selected === 'true' ? 'true' : 'false');
      }

      const panelId = item instanceof HTMLElement ? item.dataset.lookComplementsId : null;
      const count = item.querySelector('[data-look-complements-count]');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (count instanceof HTMLElement && panel instanceof HTMLElement && this.contains(panel)) {
        const selectedCount = panel.querySelectorAll('[data-look-item][data-selected="true"]').length;
        count.textContent = String(selectedCount);
        count.toggleAttribute('hidden', selectedCount === 0);
      }

      const edit = item.querySelector('[data-look-variant-edit]');
      const summary = item.querySelector('[data-look-variant-summary]');
      if (edit instanceof HTMLElement && summary instanceof HTMLElement && item instanceof HTMLElement) {
        const options = this.#optionsForItem(item);
        const values = Array.from(options?.querySelectorAll('[data-look-option-group]:not([hidden]) input:checked') ?? [],
          (input) => (input instanceof HTMLInputElement ? input.value : '')
        ).filter(Boolean);
        summary.textContent = values.join(' · ');
        edit.toggleAttribute('hidden', item.dataset.selected !== 'true' || values.length === 0);
      }
    }
  }

  /**
   * @param {number} value - Money in minor units.
   * @returns {string}
   */
  #formatMoney(value) {
    return formatMoney(value, this.dataset.moneyFormat || '{{ amount }}', this.dataset.currency || '');
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.addButton.disabled = busy || this.#selectedItems().length === 0;
    this.refs.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /** @param {string | null} message */
  #showError(message) {
    const { errorMessage } = this.refs;
    errorMessage.textContent = message ?? '';
    errorMessage.toggleAttribute('hidden', !message);
  }
}

if (!customElements.get('look-builder-component')) {
  customElements.define('look-builder-component', LookBuilderComponent);
}
